import { FileUploader, Loader } from "@/components/shared";
import { Models, RealtimeResponseEvent } from "appwrite";
import { useFetchMessages, useGetCurrentUser, useSendMessage, useSignOutAccount } from "@/lib/react-query/queries";
import { IUser } from "@/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { client, databases, storage } from "@/lib/appwrite/config";
import { DealTracker } from "./DealTracker";
import { getDealByBuyerAndSeller } from "@/lib/appwrite/api";
import { notificationService } from "@/services/NotificationService";
import { NotificationPermission } from "@/components/shared/NotificationPermission";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { FileIcon, Video, X, FileText, Send, Paperclip, ShieldCheck, ChevronDown, AlertTriangle, MapPin, Bell, Bot } from "lucide-react";
import { appwriteConfig } from "@/lib/appwrite/config";
import { isBlockedMessage, violationTracker } from "@/lib/utils/blockedPatterns";
import { getViolationType, isUserBanned, recordUserViolation } from "@/lib/appwrite/userViolationAPI";
import { INITIAL_USER, useUserContext } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { checkExistingRating, submitRating } from "@/lib/appwrite/trustScore";

// Add your Busiconn agent user ID here
const BUSICONN_AGENT_ID = "68575f4e000617f8b3f6"; 

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

interface MessageViewProps {
  selectedUser: IUser & { isConversation?: boolean, participants?: IUser[] } | null;
  currentUserId: string;
  onBack: () => void;
}

interface AppwriteMessage extends Models.Document {
  message: string;
  sender_id: string;
  recipient_id: string;
  is_read?: boolean;
  timestamp: string;
  hasAttachments?: boolean;
  attachment_ids?: string[];
  attachment_metadata_json?: string;
  is_agent_message?: boolean; // New field to identify agent messages
}

interface OptimisticMessage {
  $id: string;
  message: string;
  sender_id: string;
  recipient_id: string;
  $createdAt: string;
  is_read: boolean;
  pending: boolean;
  timestamp: string;
  hasAttachments?: boolean;
  attachment_ids: string[];
  attachment_metadata_json?: string;
  attachments?: string[];
  is_agent_message?: boolean;
}

type ChatMessage = AppwriteMessage | OptimisticMessage;

// Type guards
const isAppwriteMessage = (msg: any): msg is AppwriteMessage => {
  return msg && '$collectionId' in msg && '$databaseId' in msg;
};

const isOptimisticMessage = (msg: any): msg is OptimisticMessage => {
  return msg && msg.pending !== undefined;
};

// Helper function to check if user is Busiconn agent
const isBusiconnAgent = (userId: string): boolean => {
  return userId === BUSICONN_AGENT_ID;
};

const MessageView: React.FC<MessageViewProps> = ({ selectedUser, currentUserId, onBack }) => {
  const { toast } = useToast();
  const [newMessage, setNewMessage] = useState('');
  const { mutate: sendMessage, isLoading: isSending } = useSendMessage();
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showDealTracker, setShowDealTracker] = useState(false);
  const { data: currentUser } = useGetCurrentUser();
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [typingStatus, setTypingStatus] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  const [files, setFiles] = useState<File[]>([]);
  const [fileUrls, setFileUrls] = useState<string[]>([]);
  const [showFileUploader, setShowFileUploader] = useState(false);
  const { setUser, setIsAuthenticated } = useUserContext();
  const { mutate: signOut } = useSignOutAccount();
  const navigate = useNavigate();

  const [showRating, setShowRating] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [showPointsAnimation, setShowPointsAnimation] = useState(false);
  const [dealStatus, setDealStatus] = useState<string | null>(null);
  const [sellerId, setSellerId] = useState<string | null>(null);
  const [noticesExpanded, setNoticesExpanded] = useState(false);
  // New state to track if current user is the agent
  const isCurrentUserAgent = isBusiconnAgent(currentUserId);
let user1Id: string = currentUserId;
let user2Id: string = selectedUser?.id || '';

// If agent is viewing a conversation pair, get the actual participants
if (isCurrentUserAgent && selectedUser?.isConversation && selectedUser.participants && selectedUser.participants.length >= 2) {
  user1Id = selectedUser.participants[0].$id || selectedUser.participants[0].id || '';
  user2Id = selectedUser.participants[1].$id || selectedUser.participants[1].id || '';
}

const { data: messages, isLoading } = useFetchMessages(user1Id, user2Id, currentUserId);

  const LockIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 200 200" 
    className={className || "h-4 w-4 text-rose"}
    fill="currentColor"
    stroke="currentColor"
  >
    {/* Shield outline */}
    <path 
      d="M100 20 C 140 20, 170 40, 170 70 C 170 120, 140 160, 100 180 C 60 160, 30 120, 30 70 C 30 40, 60 20, 100 20 Z" 
      fill="none" 
      strokeWidth="6"
    />
    {/* Lock body */}
    <rect x="70" y="90" width="60" height="45" rx="5" ry="5" />
    {/* Lock shackle */}
    <path 
      d="M80 90 L80 70 C80 55, 120 55, 120 70 L120 90" 
      fill="none" 
      strokeWidth="8" 
      strokeLinecap="round"
    />
    {/* Keyhole */}
    <circle cx="100" cy="110" r="8" fill="none" strokeWidth="4"/>
    <rect x="98" y="110" width="4" height="13" />
  </svg>
);

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      fileUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [fileUrls]);

  // Check for existing deals
useEffect(() => {
  if (!selectedUser?.id) return;
  
  const checkDealAndRating = async () => {
    try {
      // Check for existing deal
      const existingDeal = await getDealByBuyerAndSeller(currentUserId, selectedUser.id) || 
                         await getDealByBuyerAndSeller(selectedUser.id, currentUserId);
      
      setShowDealTracker(!!existingDeal?.wasInitiated);
      setDealStatus(existingDeal?.status || null);
      setSellerId(existingDeal?.sellerId || null);
      // Only proceed if deal is completed
      if (existingDeal?.status === 'completed') {
        // Check if current user has already rated the other party
        const alreadyRated = await checkExistingRating(
          existingDeal.$id,       // dealId
          currentUserId,          // reviewerId (current user)
          selectedUser.id         // recipientId (the other user)
        );
        
        // Only show rating UI if deal is completed and user hasn't rated yet
        setShowRating(!alreadyRated);
      } else {
        setShowRating(false);
      }
    } catch (error) {
      console.error("Error checking deal and rating:", error);
      setShowRating(false);
    }
  };
  
  checkDealAndRating();
}, [currentUserId, selectedUser?.id]);

  // Initialize messages
  useEffect(() => {
    if (messages) {
      const filteredMessages = messages.filter((msg): msg is AppwriteMessage => 
        isAppwriteMessage(msg)
      ).map(msg => ({
        ...msg,
        timestamp: msg.timestamp || msg.$createdAt
      }));
      setLocalMessages(filteredMessages);
      scrollToBottom(false);
    }
  }, [messages]);

  // Enhanced real-time message subscription to include agent messages
  useEffect(() => {
    if (!selectedUser?.id || !currentUserId) return;

    const unsubscribe = client.subscribe(
      `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.messagesCollectionId}.documents`,
      (response: RealtimeResponseEvent<any>) => {
        const message = response.payload;
        
        // Include messages involving the agent
        let isRelevantMessage = false;

        if (isCurrentUserAgent && selectedUser?.isConversation && selectedUser.participants && selectedUser.participants.length >= 2) {
          // Agent monitoring conversation between two users
          const participant1Id = selectedUser.participants[0].$id || selectedUser.participants[0].id;
          const participant2Id = selectedUser.participants[1].$id || selectedUser.participants[1].id;
          
          isRelevantMessage = (
            (message.sender_id === participant1Id && message.recipient_id === participant2Id) ||
            (message.sender_id === participant2Id && message.recipient_id === participant1Id) ||
            (message.sender_id === BUSICONN_AGENT_ID && 
            (message.recipient_id === participant1Id || message.recipient_id === participant2Id))
          );
        } else {
        
          isRelevantMessage = (
            (message.sender_id === currentUserId && message.recipient_id === selectedUser?.id) ||
            (message.sender_id === selectedUser?.id && message.recipient_id === currentUserId) ||
            (message.sender_id === BUSICONN_AGENT_ID && 
            (message.recipient_id === currentUserId || message.recipient_id === selectedUser?.id)) ||
            ((message.sender_id === currentUserId || message.sender_id === selectedUser?.id) && 
            message.recipient_id === BUSICONN_AGENT_ID)
          );
        }
        
        if (isRelevantMessage) {
          setLocalMessages(prev => {
            // Remove optimistic version if present
            const withoutOptimistic = prev.filter(m => 
              !(isOptimisticMessage(m) && m.pending && m.message === message.message)
            );
            
            // Check if we already have this message
            if (withoutOptimistic.some(m => m.$id === message.$id)) return withoutOptimistic;
            
            const newMessage = {
              ...message,
              timestamp: message.timestamp || message.$createdAt,
              is_agent_message: message.sender_id === BUSICONN_AGENT_ID
            };

            const newMessages = [...withoutOptimistic, newMessage]
              .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            
            if (message.sender_id === selectedUser.id || message.sender_id === BUSICONN_AGENT_ID) {
              playNotificationSound();
            }
            
            return newMessages;
          });
          
          if (isAtBottom) {
            scrollToBottom(true);
          }
        }
      }
    );

    return () => unsubscribe();
  }, [selectedUser?.id, currentUserId, isAtBottom]);

  const playNotificationSound = () => {
    try {
      const audio = new Audio("/assets/sounds/notification.mp3");
      audio.volume = 0.3;
      audio.play().catch(e => console.log("Sound blocked:", e));
    } catch (error) {
      console.error("Sound error:", error);
    }
  };

  // Handle scroll position
  useEffect(() => {
    const container = messageContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setIsAtBottom(scrollHeight - scrollTop <= clientHeight + 50);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const handleTyping = useCallback(() => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    setTypingStatus(true);
    typingTimeoutRef.current = setTimeout(() => setTypingStatus(false), 1500);
  }, []);

  const scrollToBottom = (smooth: boolean) => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end'
      });
    }, 50);
  };

  const getFilePreviewUrl = (fileId: string, fileType?: string): string => {
    try {
      // For videos, use getFileView instead of getFilePreview to properly render the video
      if (fileType && fileType.startsWith('video/')) {
        return storage.getFileView(appwriteConfig.storageId, fileId);
      }
      // For other files, continue using getFilePreview
      return storage.getFilePreview(appwriteConfig.storageId, fileId);
    } catch (error) {
      console.error("Error generating file preview URL:", error);
      return ""; 
    }
  };

  // Enhanced handleSend to support agent messages
  const handleSend = useCallback(async () => {
    const messageText = newMessage.trim();

    if ((!messageText && files.length === 0) || !selectedUser?.id || isSending) return;
    
    try {
      // Skip violation checks for agent
      if (!isCurrentUserAgent) {
        // Check if user is banned (from database)
        const userIsBanned = await isUserBanned(currentUserId);
        if (userIsBanned) {
          toast({
            title: "Account Suspended",
            description: "Your account has been suspended due to repeated violations of our community guidelines. Contact contact@busiconn.com for assistance.",
            variant: "destructive"
          });
          signOut();
          setIsAuthenticated(false);
          setUser(INITIAL_USER);
          navigate("/sign-in?banned=true");
          return;
        }
        
        // Check for blocked content using your existing pattern matching
        const blockResult = isBlockedMessage(messageText);
        
        if (blockResult.isBlocked) {
          try {
            // Record the violation in the database
            const violationType = getViolationType(blockResult.reason);
            const violationResult = await recordUserViolation(currentUserId, {
              violationType,
              messageText,
              severity: blockResult.severity
            });
            
            const { violation } = violationResult;

            notificationService.createNotification({
              title: "Chat Violation Detected",
              body: `User ${currentUser?.name || 'Unknown'} violated chat rules: ${blockResult.reason}. Violation stage: ${violation.violationStage}`,
              type: "violation",
              recipient_id: "68575f4e000617f8b3f6",
              sender_id: "system",
            });
            // Determine appropriate message based on violation stage
            let title = "Message Blocked";
            let description = blockResult.reason;
            
            switch (violation.violationStage) {
              case 'banned':
                title = "Account Suspended";
                description = "Your account has been suspended due to repeated violations. Contact support if you believe this is an error.";
                break;
                
              case 'flagged':
                title = "Account Flagged";
                const flagDate = violation.flaggedUntil ? new Date(violation.flaggedUntil).toLocaleDateString('en-GB') : 'N/A';
                description = `${blockResult.reason}. Your account is now flagged until ${flagDate}. Further violations will result in account suspension. (Total violations: ${violation.violationCount})`;
                break;
                
              default: // 'normal'
                if (blockResult.severity === 'violation') {
                  title = "Content Violation";
                  description = `${blockResult.reason}. This violation has been recorded. (Total violations: ${violation.violationCount})`;
                } else {
                  title = "Content Warning";
                  description = `${blockResult.reason}. Please follow our community guidelines.`;
                }
            }
            
            toast({
              title,
              description,
              variant: "destructive"
            });
            
            // Clear the message input for violations (not warnings)
            if (blockResult.severity === 'violation') {
              setNewMessage('');
            }
            
            return; // Stop execution - message will not be sent
            
          } catch (dbError) {
            console.error('Error recording violation to database:', dbError);
            
            // Fallback to local tracking if database fails
            const userStatus = violationTracker.recordViolation(currentUserId, blockResult.severity);
        
            let title = "Message Blocked";
            let description = blockResult.reason;
            
            if (userStatus.isBanned) {
              title = "Account Suspended";
              description = "Your account has been suspended due to repeated violations. Contact support if you believe this is an error.";
            } else if (userStatus.isFlagged) {
              title = "Account Flagged";
              description = `${blockResult.reason}. Your account is now flagged. Further violations will result in account suspension. (Warnings: ${userStatus.warnings}, Violations: ${userStatus.violations})`;
            } else if (blockResult.severity === 'warning') {
              description = `${blockResult.reason}. This is a warning - please follow our community guidelines.`;
            }
            
            toast({
              title,
              description,
              variant: "destructive"
            });
            
            // Clear the message input for violations
            if (blockResult.severity === 'violation') {
              setNewMessage('');
            }
            
            return; // Stop execution here - message will not be sent
          }
        }
      }

      // === Your existing message sending code continues below - unchanged ===
      
      const optimisticMetadata = files.map(file => ({
        name: file.name,
        type: file.type,
        size: file.size
      }));

      const tempId = `temp-${Date.now()}`;
      let recipientId: string = selectedUser?.id || '';
      if (isCurrentUserAgent && selectedUser?.isConversation && selectedUser.participants && selectedUser.participants.length >= 2) {
        // Send to the first participant (you can enhance this to choose recipient)
        recipientId = selectedUser.participants[0].$id || selectedUser.participants[0].id || '';
      }
      const newMsg: OptimisticMessage = {
        $id: tempId,
        message: messageText,
        sender_id: currentUserId,
        recipient_id: recipientId,
        $createdAt: new Date().toISOString(),
        is_read: false,
        pending: true,
        timestamp: new Date().toISOString(),
        hasAttachments: files.length > 0,
        attachments: files.length > 0 ? fileUrls : undefined,
        attachment_ids: [],
        attachment_metadata_json: files.length > 0 ? JSON.stringify(optimisticMetadata) : "",
        is_agent_message: isCurrentUserAgent
      };

      setLocalMessages(prev => [...prev, newMsg]);
      setNewMessage('');
      const filesToSend = [...files];
      setFiles([]);
      setFileUrls([]);
      setShowFileUploader(false);
      scrollToBottom(true);
      inputRef.current?.focus();

      try {
        await sendMessage({
          senderId: currentUserId,
          recipientId: recipientId,
          message: messageText,
          attachments: filesToSend.length > 0 ? filesToSend : undefined,
          isAgentMessage: isCurrentUserAgent // Add this field to identify agent messages
        });

        notificationService.createNotification({
          title: `${isCurrentUserAgent ? 'Busiconn Support' : currentUser?.name || 'Someone'} sent a message`,
          body: messageText || (filesToSend.length > 0 ? "Sent an attachment" : ""),
          type: "message",
          recipient_id: selectedUser.id,
          sender_id: currentUserId
        });

        setLocalMessages(prev => 
          prev.map(msg => 
            msg.$id === tempId ? { ...msg, pending: false } : msg
          )
        );
      } catch (error) {
        console.error("Error sending message:", error);
        setLocalMessages(prev => prev.filter(msg => msg.$id !== tempId));
        toast({
          title: "Failed to send message",
          description: "Please try again",
          variant: "destructive"
        });
      }
      
    } catch (generalError) {
      console.error("General error in handleSend:", generalError);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    }
  }, [newMessage, selectedUser, currentUserId, isSending, currentUser?.name, toast, files, fileUrls, isCurrentUserAgent]);

  const markMessagesAsRead = useCallback(async (messageIds: string[]) => {
    if (!selectedUser?.id || messageIds.length === 0) return;
    
    try {
      await Promise.all(
        messageIds.map(id => 
          databases.updateDocument(
            appwriteConfig.databaseId,
            appwriteConfig.messagesCollectionId,
            id,
            { is_read: true }
          )
        )
      );
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  }, [selectedUser?.id]);

  useEffect(() => {
    if (!selectedUser?.id || !currentUserId) return;

    const unreadMessages = localMessages.filter((msg): msg is AppwriteMessage => 
      isAppwriteMessage(msg) && 
      msg.recipient_id === currentUserId && 
      msg.sender_id === selectedUser.id && 
      !msg.is_read
    );

    if (unreadMessages.length > 0) {
      markMessagesAsRead(unreadMessages.map(msg => msg.$id));
    }
  }, [localMessages, selectedUser?.id, currentUserId, markMessagesAsRead]);

  const handleFileChange = (newFiles: File[]) => {
    // Clean up previous URLs
    fileUrls.forEach(url => URL.revokeObjectURL(url));
    
    const newUrls = newFiles.map(file => URL.createObjectURL(file));
    setFiles(newFiles);
    setFileUrls(newUrls);
  };

  const renderAttachmentPreview = (url: string, type: string, index: number, onRemove: () => void) => {
    if (type.startsWith('image/')) {
      return (
        <div className="relative inline-block">
          <img 
            src={url} 
            alt={`Preview ${index}`}
            className="w-16 h-16 object-cover rounded-lg"
          />
          <button
            onClick={onRemove}
            className="absolute -top-2 -right-2 bg-rose rounded-full p-1"
          >
            <X className="h-3 w-3 text-white" />
          </button>
        </div>
      );
    } else if (type.startsWith('video/')) {
      return (
        <div className="relative inline-block">
          <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
            <Video className="h-5 w-5 text-gray-500 dark:text-gray-300" />
          </div>
          <button
            onClick={onRemove}
            className="absolute -top-2 -right-2 bg-rose rounded-full p-1"
          >
            <X className="h-3 w-3 text-white" />
          </button>
        </div>
      );
    } else if (type.startsWith('application/pdf')) {
      return (
        <div className="relative inline-block">
          <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
            <FileText className="h-5 w-5 text-gray-500 dark:text-gray-300" />
          </div>
          <button
            onClick={onRemove}
            className="absolute -top-2 -right-2 bg-rose rounded-full p-1"
          >
            <X className="h-3 w-3 text-white" />
          </button>
        </div>
      );
    } else {
      return (
        <div className="relative inline-block">
          <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
            <FileIcon className="h-5 w-5 text-gray-500 dark:text-gray-300" />
          </div>
          <button
            onClick={onRemove}
            className="absolute -top-2 -right-2 bg-rose rounded-full p-1"
          >
            <X className="h-3 w-3 text-white" />
          </button>
        </div>
      );
    }
  };

  const renderAttachment = (fileId: string, metadata: any, index: number) => {
    const fileType = metadata?.type || 'application/octet-stream';
    const filePreviewUrl = getFilePreviewUrl(fileId, fileType);
    
    if (fileType.startsWith('image/')) {
      return (
        <div className="mt-2">
          <img 
            src={filePreviewUrl}
            alt={metadata?.name || `Image ${index}`}
            className="max-h-[200px] max-w-full rounded-lg object-contain cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => window.open(filePreviewUrl, '_blank')}
          />
        </div>
      );
    } else if (fileType.startsWith('video/')) {
      return (
        <div className="mt-2">
          <video 
            controls 
            src={filePreviewUrl}
            className="rounded-lg max-h-[200px] max-w-full"
            preload="metadata"
          />
        </div>
      );
    } else {
      return (
        <div className="mt-2">
          <a
            href={filePreviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {fileType.startsWith('application/pdf') ? (
              <FileText className="mr-2 flex-shrink-0" size={20} />
            ) : (
              <FileIcon className="mr-2 flex-shrink-0" size={20} />
            )}
            <div className="truncate">
              <p className="truncate text-sm font-medium">{metadata?.name || `File ${index}`}</p>
              {metadata?.size && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatFileSize(metadata.size)}
                </p>
              )}
            </div>
          </a>
        </div>
      );
    }
  };

  if (!selectedUser) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-400">Select a user to start chatting</p>
      </div>
    );
  }

  if (isLoading) return <Loader />;

  return (
    <div className="flex flex-1 flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Chat header */}
      <header className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10">
        <div className="flex items-center space-x-3">
          <button 
            onClick={onBack} 
            className="md:hidden text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-full"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex items-center">
          {isCurrentUserAgent && selectedUser?.isConversation ? (
            <div className="flex -space-x-1 mr-3">
              <div className="w-7 h-7 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center text-blue-500 dark:text-blue-300 text-sm font-medium border-2 border-white dark:border-gray-800">
                {selectedUser.participants?.[0]?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="w-7 h-7 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center text-green-500 dark:text-green-300 text-sm font-medium border-2 border-white dark:border-gray-800">
                {selectedUser.participants?.[1]?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="w-7 h-7 bg-purple-100 dark:bg-purple-800 rounded-full flex items-center justify-center text-purple-500 dark:text-purple-300 text-sm font-medium border-2 border-white dark:border-gray-800">
                <Bot className="h-4 w-4" />
              </div>
            </div>
          ) : (
            <div className={`w-9 h-9 ${isCurrentUserAgent ? 'bg-purple-100 dark:bg-purple-800' : 'bg-blue-100 dark:bg-blue-800'} rounded-full flex items-center justify-center ${isCurrentUserAgent ? 'text-purple-500 dark:text-purple-300' : 'text-blue-500 dark:text-blue-300'} font-medium`}>
              {isCurrentUserAgent ? <Bot className="h-5 w-5" /> : selectedUser.name?.charAt(0).toUpperCase()}
            </div>
          )}
            <div className="ml-2">
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {isCurrentUserAgent && selectedUser?.isConversation 
                ? selectedUser.name // Already formatted as "User1 ↔ User2"
                : isCurrentUserAgent 
                  ? "Busiconn Support" 
                  : selectedUser.name}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {isCurrentUserAgent && selectedUser?.isConversation 
                ? "Monitoring conversation"
                : typingStatus 
                  ? "typing..." 
                  : "online"}
            </p>
            </div>
          </div>
        </div>
        <NotificationPermission />
      </header>

      {/* Deal Tracker */}
      {showDealTracker && (
        <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900/30">
          <DealTracker
            buyerId={currentUserId}
            sellerId={selectedUser.id}
            currentUserId={currentUserId}
            isCollapsible={true}
          />
        </div>
      )}

      {/* Agent monitoring notice - only show for non-agent users */}
      {showDealTracker && !isCurrentUserAgent && (
        <div className="px-4 py-2 bg-indigo-50 dark:bg-indigo-900/20 border-b border-indigo-100 dark:border-indigo-900/30 flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center mr-2">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
              </svg>
            </div>
            <p className="text-sm text-indigo-600 dark:text-indigo-300">
              <span className="font-medium">Busiconn Agent</span> is monitoring this chat
            </p>
          </div>
        </div>
      )}

      <div className="border-b border-gray-200 dark:border-gray-700">
  <button 
    onClick={() => setNoticesExpanded(!noticesExpanded)}
    className="w-full px-4 py-2 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
  >
    <div className="flex items-center">
      <ShieldCheck className="h-4 w-4 mr-2 text-green-500" />
      <span>Security Notices</span>
      {showDealTracker && dealStatus === 'paid' && currentUserId===sellerId && (
      <div className="relative w-5 h-5">
        <Bell className="text-rose w-5 h-5" />
        <span className="absolute -top-1 -right-1 bg-rose text-white text-[10px] rounded-full w-3 h-3 flex items-center justify-center">
          1
        </span>
      </div>
      )}
    </div>
    <ChevronDown className={`h-4 w-4 transition-transform ${noticesExpanded ? 'transform rotate-180' : ''}`} />
  </button>

  {noticesExpanded && (
    <div className="px-4 pb-3 space-y-3">
      {/* Privacy Warning */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
        <p className="text-xs font-medium text-yellow-800 dark:text-yellow-200">
          <span className="inline-flex items-center">
            <AlertTriangle className="h-3.5 w-3.5 mr-1" />
            Never Share Personal Info
          </span>
        </p>
        <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
          Avoid sharing personal info. It can get your account blocked and TrustScore cut.
        </p>
      </div>

      {/* Payment Status - Only show if deal exists */}
      {showDealTracker && (
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
          <p className="text-xs font-medium text-blue-800 dark:text-blue-200">
            <span className="inline-flex items-center">
              <LockIcon />
              Payment Secured
            </span>
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
            Funds held until delivery confirmation. 
            <button 
              onClick={() => window.open('/termandcondition', '_blank')}
              className="ml-1 underline"
            >
              Learn more
            </button>
          </p>
        </div>
      )}

      {/* Address Notice - Only for sellers */}
      {showDealTracker && dealStatus === 'paid' && currentUserId===sellerId && (
        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
          <p className="text-xs font-medium text-purple-800 dark:text-purple-200">
            <span className="inline-flex items-center">
              <MapPin className="h-3.5 w-3.5 mr-1" />
              Next Steps
            </span>
          </p>
          <p className="text-xs text-purple-600 dark:text-purple-300 mt-1">
            Wait for buyer to share delivery address through secure channel.
          </p>
        </div>
      )}
    </div>
  )}
</div>

      {/* Messages container */}
      <div 
        ref={messageContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600"
      >
        {localMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500 dark:text-gray-400">
              <p>No messages yet</p>
              <p className="text-sm mt-1">Start a conversation with {selectedUser.name}</p>
            </div>
          </div>
        ) : (
          localMessages.map((msg) => {
            const isSender = msg.sender_id === currentUserId;
            const isOptimistic = isOptimisticMessage(msg) && msg.pending;
            const isAgentMessage = msg.is_agent_message || isBusiconnAgent(msg.sender_id);
            
            // Parse attachment metadata for non-optimistic messages
            let attachmentMetadata = [];
              if (!isOptimisticMessage(msg) && msg.attachment_metadata_json) { // Changed from attachment_metadata
                try {
                  attachmentMetadata = JSON.parse(msg.attachment_metadata_json);
                } catch (e) {
                  console.error("Failed to parse attachment metadata", e);
                  attachmentMetadata = [];
                }
              } else if (isOptimisticMessage(msg) && msg.attachment_metadata_json) {
                try {
                  attachmentMetadata = JSON.parse(msg.attachment_metadata_json);
                } catch (e) {
                  console.error("Failed to parse optimistic attachment metadata", e);
                  attachmentMetadata = [];
                }
              }
            
            return (
              <div
                key={msg.$id}
                className={`flex ${isSender ? 'justify-end' : 'justify-start'}`}
              >
                <div className="flex items-start space-x-2 max-w-xs lg:max-w-md">
                  {/* Agent avatar for agent messages */}
                  {!isSender && isAgentMessage && (
                    <div className="w-8 h-8 bg-purple-100 dark:bg-purple-800 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Bot className="h-4 w-4 text-purple-500 dark:text-purple-300" />
                    </div>
                  )}
                  
                  <div
                    className={`px-4 py-2 rounded-lg ${
                      isSender
                        ? isAgentMessage
                          ? 'bg-purple-500 text-white rounded-br-none' // Agent as sender
                          : 'bg-blue-500 text-white rounded-br-none'   // Regular user as sender
                        : isAgentMessage
                          ? 'bg-purple-100 dark:bg-purple-900 text-purple-900 dark:text-purple-100 rounded-bl-none border border-purple-200 dark:border-purple-700' // Agent as receiver
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-none' // Regular user as receiver
                    } ${isOptimistic ? 'opacity-70' : ''}`}
                  >
                    {/* Agent label for agent messages */}
                    {isAgentMessage && (
                      <div className="flex items-center mb-1">
                        <Bot className="h-3 w-3 mr-1" />
                        <span className="text-xs font-medium opacity-75">Busiconn Support</span>
                      </div>
                    )}
                    
                    {/* Message text */}
                    {msg.message && <p className="text-sm break-words">{msg.message}</p>}
                    
                    {/* Attachments for real messages */}
                    {!isOptimisticMessage(msg) && msg.hasAttachments && msg.attachment_ids?.map((fileId, index) => {
                      const metadata = attachmentMetadata[index] || { type: 'application/octet-stream' };
                      return renderAttachment(fileId, metadata, index);
                    })}

                    {/* Attachment preview for optimistic updates */}
                    {isOptimisticMessage(msg) && msg.attachments?.map((url, index) => {
                      const metadata = attachmentMetadata[index] || { 
                        type: url.includes("video") ? "video/mp4" : "image/jpeg" 
                      };
                      
                      if (metadata.type.startsWith('image/')) {
                        return (
                          <div key={index} className="mt-2">
                            <img 
                              src={url} 
                              alt={`Attachment ${index}`}
                              className="max-h-[200px] max-w-full rounded-lg object-contain"
                            />
                          </div>
                        );
                      } else if (metadata.type.startsWith('video/')) {
                        return (
                          <div key={index} className="mt-2">
                            <video 
                              controls 
                              src={url} 
                              className="max-h-[200px] rounded-lg max-w-full"
                              preload="metadata"
                            />
                          </div>
                        );
                      } else {
                        return (
                          <div key={index} className="mt-2">
                            <div className="flex items-center p-2 bg-gray-100 dark:bg-gray-600 rounded-lg">
                              <FileIcon className="mr-2 h-4 w-4" />
                              <span className="text-sm">Attachment {index + 1}</span>
                            </div>
                          </div>
                        );
                      }
                    })}

                    {/* Message timestamp */}
                    <p className={`text-xs mt-1 text-right ${
                      isSender 
                        ? isAgentMessage
                          ? 'text-purple-100'
                          : 'text-blue-100'
                        : isAgentMessage
                          ? 'text-purple-500 dark:text-purple-400'
                          : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {formatDistanceToNow(new Date(msg.timestamp || msg.$createdAt), { addSuffix: true })}
                      {isOptimistic && ' • Sending'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Rating component - hide for agent */}
      {showRating && !isCurrentUserAgent && (
        <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 relative">
          <div className="flex flex-col items-center">
            <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-white">Rate your experience</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">How was your deal with {selectedUser?.name}?</p>
            
            <div className="flex space-x-2 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setSelectedRating(star)}
                  className={`text-3xl transition-transform duration-100 ${
                    selectedRating >= star 
                      ? 'text-yellow-400 transform scale-110' 
                      : 'text-gray-300 dark:text-gray-500 hover:text-yellow-300'
                  }`}
                >
                  ★
                </button>
              ))}
            </div>
            
            <div className="w-full mb-4">
              <textarea
                placeholder="Type a message (optional)"
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                className="w-full px-3 py-2 text-sm border text-gray-700 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                rows={3}
              />
            </div>
            
            <button
              onClick={async () => {
                if (selectedRating > 0) {
                  try {
                    const existingDeal = await getDealByBuyerAndSeller(currentUserId, selectedUser.id) || 
                              await getDealByBuyerAndSeller(selectedUser.id, currentUserId);
                    // Submit the rating
                    await submitRating(
                      currentUser?.name,
                      existingDeal!.$id, // The completed deal ID
                      currentUserId,       // Reviewer ID (current user)
                      selectedUser.id, // Recipient ID (the other user in chat)
                      selectedRating,
                      ratingComment
                    );
                    
                    // Show points animation if rating is positive
                    if (selectedRating >= 4) {
                      setShowPointsAnimation(true);
                      setTimeout(() => setShowPointsAnimation(false), 2000);
                    }
                    
                    // Close the rating component
                    setShowRating(false);
                    toast({
                        title: "Rating",
                        description: "Rating submitted successfully!"
                      });
                    
                    // Reset form
                    setSelectedRating(0);
                    setRatingComment('');
                  } catch (error) {
                    console.error("Error submitting rating:", error);
                      toast({
                        title: "Rating",
                        description: "Failed to submit rating. Please try again.",
                        variant: "destructive"
                      });
                  }
                }
              }}
              disabled={selectedRating === 0}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                selectedRating > 0 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md' 
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              Submit Rating
            </button>
          </div>
          
          {/* Points animation */}
          {showPointsAnimation && selectedRating >= 4 && (
            <div className="absolute -bottom-4 -right-4">
              <div className="animate-float-up bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 font-bold px-3 py-1 rounded-full text-sm shadow-md">
                +5 TrustPoints
              </div>
            </div>
          )}
        </div>
      )}

      {/* Input area */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 sticky bottom-0">
        {/* File preview section */}
        {fileUrls.length > 0 && (
          <div className="mb-3 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <div className="flex flex-wrap gap-2">
              {fileUrls.map((url, index) => {
                const file = files[index];
                return renderAttachmentPreview(
                  url, 
                  file.type, 
                  index, 
                  () => {
                    const newFiles = [...files];
                    const newUrls = [...fileUrls];
                    newFiles.splice(index, 1);
                    newUrls.splice(index, 1);
                    setFiles(newFiles);
                    setFileUrls(newUrls);
                    URL.revokeObjectURL(url);
                  }
                );
              })}
            </div>
          </div>
        )}

        {showFileUploader && (
          <div className="mb-3 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <FileUploader 
              fieldChange={handleFileChange}
              mediaUrl={fileUrls}
              mode="message"
              maxFiles={5}
            />
          </div>
        )}

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowFileUploader(!showFileUploader)}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                handleTyping();
              }}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder={isCurrentUserAgent ? "Type a support message..." : "Type a message..."}
              disabled={isSending}
              className="w-full p-2 text-gray-900 dark:text-white rounded-full border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <button
            onClick={handleSend}
            disabled={(!newMessage.trim() && files.length === 0) || isSending}
            className={`p-2 rounded-full ${
              (newMessage.trim() || files.length > 0) && !isSending
                ? isCurrentUserAgent
                  ? 'bg-purple-500 hover:bg-purple-600 text-white' // Agent send button
                  : 'bg-blue-500 hover:bg-blue-600 text-white'     // Regular send button
                : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            } transition-colors`}
          >
            {isSending ? (
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-b-transparent border-white" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageView;